import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useSidebar } from "../../hooks/useSidebar";
import { isAdminRole } from "../../utils/roles.utils";
import {
  contratosApi,
  Contrato,
  ContratoInput,
  ContratoArchivo,
} from "../../services/contratos.api";
import { usersApi } from "../../services/users.api";
import { propiedadesApi } from "../../services/propiedades.api";

interface ClienteOption {
  id: number;
  nombre_usuario: string;
}

interface PropiedadOption {
  id: number;
  nombrepropiedad: string;
}

export function useContratosLogic() {
  const { user } = useAuth();
  const { sidebarOpen, toggleSidebar } = useSidebar();

  // `user.rol` es string escalar (ver CLAUDE.md §3.3 y roles.utils.ts).
  // `isAdminRole` acepta string|string[]|null; NO envolver en Array.isArray
  // porque `[]` se normaliza a "cliente" y rompe el flujo admin.
  const isAdmin = isAdminRole(user?.rol);

  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [propiedades, setPropiedades] = useState<PropiedadOption[]>([]);

  // ── Fetch contratos ────────────────────────────────────────────────────────

  const fetchContratos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contratosApi.getAll();
      setContratos(data);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch dropdown options (admin only) ────────────────────────────────────

  const fetchOptions = useCallback(async () => {
    if (!isAdmin) return;

    const [clientesRes, propiedadesRes] = await Promise.all([
      usersApi.getClients(),
      propiedadesApi.getNombres(),
    ]);

    if (clientesRes) {
      setClientes(
        clientesRes.map((c: any) => ({
          id: c.id,
          nombre_usuario: c.nombre_usuario ?? c.nombre ?? `Cliente #${c.id}`,
        })),
      );
    }

    if (propiedadesRes?.data) {
      setPropiedades(propiedadesRes.data);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchContratos();
    fetchOptions();
  }, [fetchContratos, fetchOptions]);

  // ── Realtime: refetch cuando el backend emite DOCUMENT_CHANGED ───────────
  //
  // Requisito explícito del usuario: cuando un admin/superadmin sube o edita
  // un documento del cliente (o le adjunta un archivo), el panel /documentos
  // del cliente debe actualizarse en tiempo real, sin pedirle que refresque.
  //
  // Regla de filtrado:
  //  - Admin/superadmin: refetch siempre (le interesan todos los cambios).
  //  - Cliente: solo si el documento afectado es suyo (id_cliente === user.id).
  //
  // Se usa el socket compartido expuesto por [`WebSocketContext`](
  // ../../context/WebSocketContext.tsx), mismo patrón que
  // `CALENDAR_CHANGED`. El refetch es ligero (un GET /contratos) y el
  // backend ya filtra por rol, así que un admin recibe todo y un cliente
  // solo lo suyo.
  const { socket } = useWebSocket();
  useEffect(() => {
    if (!socket) return;
    const onMessage = (ev: MessageEvent) => {
      let data: any;
      try {
        data = JSON.parse(ev.data);
      } catch {
        return; // no era JSON, ignorar
      }
      if (data?.type !== "DOCUMENT_CHANGED") return;
      if (!isAdmin) {
        // Para cliente, solo si el documento es suyo.
        if (data.id_cliente !== user?.id) return;
      }
      fetchContratos();
    };
    socket.addEventListener("message", onMessage);
    return () => socket.removeEventListener("message", onMessage);
  }, [socket, isAdmin, user?.id, fetchContratos]);

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleCreate = async (data: ContratoInput) => {
    const created = await contratosApi.create(data);
    if (created) {
      await fetchContratos();
    }
    return created;
  };

  const handleUpdate = async (id: number, data: Partial<ContratoInput>) => {
    const updated = await contratosApi.update(id, data);
    if (updated) {
      await fetchContratos();
    }
    return updated;
  };

  const handleDelete = async (id: number) => {
    const ok = await contratosApi.delete(id);
    if (ok) {
      setContratos((prev) => prev.filter((c) => c.id !== id));
    }
  };

  // ── Archivo handlers ───────────────────────────────────────────────────────

  const handleUploadArchivo = async (
    id: number,
    file: File,
  ): Promise<ContratoArchivo | null> => {
    const archivo = await contratosApi.uploadArchivo(id, file);
    if (archivo) {
      await fetchContratos();
    }
    return archivo;
  };

  const handleDeleteArchivo = async (id: number, key: string) => {
    const updatedArchivos = await contratosApi.deleteArchivo(id, key);
    setContratos((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, archivos: updatedArchivos } : c,
      ),
    );
  };

  // Para el cliente, la lista de propiedades disponibles para filtrar se
  // deriva de sus propios documentos (el backend `getAllPropertyNames` es
  // global — no queremos exponerle todas las propiedades). Para admin,
  // usamos la lista completa ya cargada en `propiedades`.
  const propiedadesParaFiltro = useMemo(() => {
    if (isAdmin) return propiedades;
    const map = new Map<number, { id: number; nombrepropiedad: string }>();
    for (const c of contratos) {
      if (c.propiedad?.id && c.propiedad.nombrepropiedad) {
        map.set(c.propiedad.id, {
          id: c.propiedad.id,
          nombrepropiedad: c.propiedad.nombrepropiedad,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.nombrepropiedad.localeCompare(b.nombrepropiedad),
    );
  }, [isAdmin, propiedades, contratos]);

  return {
    contratos,
    loading,
    isAdmin,
    clientes,
    propiedades,
    propiedadesParaFiltro,
    sidebarOpen,
    toggleSidebar,
    fetchContratos,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleUploadArchivo,
    handleDeleteArchivo,
  };
}
